/**
 * Sign-in health, and the lookup support needs when someone cannot get in.
 *
 * Both halves of this page exist because of the same gap: a failed sign-in used to leave
 * no trace (only successes were logged), and admin search only ever reached `users`, so
 * the 106k unclaimed legacy students were invisible and an account whose only
 * recognisable detail was the PARENT's email could not be found at all.
 */
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { Loader2, Search, Send, ShieldAlert } from "lucide-react";

interface OutcomeRow { outcome: string; identifier_kind: string; n: number }
interface DailyRow { day: string; succeeded: number; failed: number; unknown_username: number }
interface StuckRow { identifier_masked: string; identifier_kind: string; attempts: number; last_attempt: string; outcomes: string[] }
interface FinderRow { lookups: number; matched: number }
interface IssuesResponse { days: number; byOutcome: OutcomeRow[]; daily: DailyRow[]; stuck: StuckRow[]; finder: FinderRow }

interface IdentityRow {
  source: "academy" | "legacy";
  userId: string | null;
  legacyId: number | null;
  name: string;
  email: string;
  username: string | null;
  grade: number | null;
  school: string | null;
  parentEmail: string | null;
  parentPhone: string | null;
  matchedOn: string;
  claimed: boolean;
  lastActiveAt: string | null;
}

const OUTCOME_LABEL: Record<string, string> = {
  success: "Signed in",
  legacy_claimed: "Legacy account claimed",
  bad_password: "Right account, wrong password",
  no_match: "No account matched what was typed",
  legacy_bad_password: "Legacy account, wrong password",
};

const isFailure = (outcome: string) => outcome !== "success" && outcome !== "legacy_claimed";

export default function AdminSignIn() {
  const [days, setDays] = useState("14");
  const [query, setQuery] = useState("");
  const [submitted, setSubmitted] = useState("");
  const { toast } = useToast();

  const { data: issues, isLoading } = useQuery<IssuesResponse>({
    queryKey: [`/api/admin/login-issues?days=${days}`],
  });

  const { data: lookup, isFetching: isLooking } = useQuery<{ results: IdentityRow[] }>({
    queryKey: [`/api/admin/identity/lookup?q=${encodeURIComponent(submitted)}`],
    enabled: submitted.length >= 3,
  });

  const sendRecovery = useMutation({
    mutationFn: async (row: IdentityRow) => {
      const body = row.source === "academy" ? { userId: row.userId } : { legacyEmail: row.email };
      const res = await apiRequest("POST", "/api/admin/identity/send-recovery", body);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send");
      return data as { kind: string; maskedEmail: string };
    },
    onSuccess: (data) => toast({
      title: data.kind === "legacy_claim" ? "Claim link sent" : "Reset link sent",
      description: `Sent to ${data.maskedEmail}. It expires in an hour.`,
    }),
    onError: (err: any) => toast({ title: "Couldn't send", description: err.message, variant: "destructive" }),
  });

  const totals = (issues?.byOutcome ?? []).reduce(
    (acc, r) => {
      acc.all += r.n;
      if (isFailure(r.outcome)) acc.failed += r.n;
      if (r.outcome === "no_match" && r.identifier_kind === "username") acc.unknownUsername += r.n;
      if (r.outcome === "legacy_claimed") acc.legacyClaims += r.n;
      return acc;
    },
    { all: 0, failed: 0, unknownUsername: 0, legacyClaims: 0 },
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold" data-testid="text-page-title">Sign-in health</h1>
          <p className="text-muted-foreground">What people type when they can't get in, and who to help.</p>
        </div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-40" data-testid="select-days">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {["7", "14", "30", "90"].map(d => <SelectItem key={d} value={d}>Last {d} days</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="health">
        <TabsList>
          <TabsTrigger value="health" data-testid="tab-health">Health</TabsTrigger>
          <TabsTrigger value="lookup" data-testid="tab-lookup">Find someone</TabsTrigger>
        </TabsList>

        <TabsContent value="health" className="space-y-6 pt-4">
          {isLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                {[
                  { label: "Attempts", value: totals.all, hint: "Sign-ins tried" },
                  { label: "Failed", value: totals.failed, hint: totals.all ? `${Math.round((totals.failed / totals.all) * 100)}% of attempts` : "–" },
                  { label: "Unknown username", value: totals.unknownUsername, hint: "Typed a handle we don't have" },
                  { label: "Legacy claims", value: totals.legacyClaims, hint: "Old accounts brought across" },
                  { label: "Finder used", value: issues?.finder?.lookups ?? 0, hint: `${issues?.finder?.matched ?? 0} found their account` },
                ].map(stat => (
                  <Card key={stat.label}>
                    <CardContent className="pt-6">
                      <p className="text-sm text-muted-foreground">{stat.label}</p>
                      <p className="text-3xl font-bold" data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, "-")}`}>{stat.value}</p>
                      <p className="text-xs text-muted-foreground mt-1">{stat.hint}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>What happened</CardTitle>
                  <CardDescription>
                    Every sign-in attempt in the window, by outcome and by what was typed.
                    Account-finder lookups are counted separately above.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Outcome</TableHead>
                        <TableHead>They typed</TableHead>
                        <TableHead className="text-right">Attempts</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(issues?.byOutcome ?? []).map((r, i) => (
                        <TableRow key={i} data-testid={`row-outcome-${i}`}>
                          <TableCell>
                            <Badge variant={isFailure(r.outcome) ? "destructive" : "secondary"}>
                              {OUTCOME_LABEL[r.outcome] ?? r.outcome}
                            </Badge>
                          </TableCell>
                          <TableCell className="capitalize text-muted-foreground">{r.identifier_kind}</TableCell>
                          <TableCell className="text-right font-medium">{r.n}</TableCell>
                        </TableRow>
                      ))}
                      {!issues?.byOutcome?.length && (
                        <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-8">Nothing recorded yet.</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5" /> Still locked out</CardTitle>
                  <CardDescription>
                    Tried more than once and never got in. Identifiers are masked. The table stores
                    a hash, not the address.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Identifier</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Outcomes</TableHead>
                        <TableHead className="text-right">Tries</TableHead>
                        <TableHead className="text-right">Last try</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(issues?.stuck ?? []).map((r, i) => (
                        <TableRow key={i} data-testid={`row-stuck-${i}`}>
                          <TableCell className="font-mono text-sm">{r.identifier_masked}</TableCell>
                          <TableCell className="capitalize text-muted-foreground">{r.identifier_kind}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {r.outcomes.map(o => OUTCOME_LABEL[o] ?? o).join(", ")}
                          </TableCell>
                          <TableCell className="text-right font-medium">{r.attempts}</TableCell>
                          <TableCell className="text-right text-muted-foreground text-sm">
                            {new Date(r.last_attempt).toLocaleDateString()}
                          </TableCell>
                        </TableRow>
                      ))}
                      {!issues?.stuck?.length && (
                        <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Nobody is stuck. Good.</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="lookup" className="space-y-4 pt-4">
          <Card>
            <CardHeader>
              <CardTitle>Find someone</CardTitle>
              <CardDescription>
                Searches Academy accounts and the unclaimed legacy roll, by email, username,
                student name, or the parent's email or phone.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <form
                className="flex gap-2"
                onSubmit={(e) => { e.preventDefault(); setSubmitted(query.trim()); }}
              >
                <Input
                  placeholder="Email, username, name, or parent's phone"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  data-testid="input-lookup"
                />
                <Button type="submit" disabled={query.trim().length < 3} data-testid="button-lookup">
                  <Search className="h-4 w-4 mr-2" /> Search
                </Button>
              </form>

              {isLooking && <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>}

              {!isLooking && lookup && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Username</TableHead>
                      <TableHead>Matched on</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lookup.results.map((r, i) => (
                      <TableRow key={`${r.source}-${r.userId ?? r.legacyId}`} data-testid={`row-result-${i}`}>
                        <TableCell>
                          <div className="font-medium">{r.name || "–"}</div>
                          <div className="text-xs text-muted-foreground">
                            {[r.grade ? `Grade ${r.grade}` : null, r.school].filter(Boolean).join(" · ") || "–"}
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {r.email}
                          {r.parentEmail && <div className="text-xs text-muted-foreground">parent: {r.parentEmail}</div>}
                        </TableCell>
                        <TableCell className="font-mono text-sm">{r.username ?? "–"}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{r.matchedOn}</TableCell>
                        <TableCell>
                          {r.source === "academy"
                            ? <Badge variant="secondary">Academy</Badge>
                            : <Badge variant={r.claimed ? "secondary" : "outline"}>{r.claimed ? "Legacy (claimed)" : "Legacy (unclaimed)"}</Badge>}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={sendRecovery.isPending || (r.source === "legacy" && r.claimed)}
                            onClick={() => sendRecovery.mutate(r)}
                            data-testid={`button-send-${i}`}
                          >
                            <Send className="h-4 w-4 mr-2" />
                            {r.source === "academy" ? "Send reset" : "Send claim link"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {!lookup.results.length && (
                      <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No match on either roll.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
